<template>
  <div class="widget-toolbar align-content-center">
    <p>Add Widget to Template:</p>

    <div class="widget-buttons">
      <div class="widget-button" title="Text" @click.prevent="createRichText">
        <i class="material-icons-outlined align-middle">edit_note</i>
        <span>Text</span>
      </div>

      <div
        class="widget-button"
        title="Image"
        @click.prevent="openContentBrowser(WIDGET_CONTENT_TYPE.IMAGE)"
      >
        <i class="material-icons-outlined align-middle">image</i>
        <span>Image</span>
      </div>

      <div
        class="widget-button"
        title="Video"
        @click.prevent="openContentBrowser(WIDGET_CONTENT_TYPE.VIDEO)"
      >
        <i class="material-icons-outlined align-middle">play_circle_outline</i>
        <span>Video</span>
      </div>

      <div class="widget-button" title="Time" @click.prevent="showCreateTimeModal = true">
        <i class="material-icons-outlined align-middle">schedule</i>
        <span>Time</span>
      </div>

      <div class="widget-button" title="Date" @click.prevent="showCreateDateModal = true">
        <i class="material-icons-outlined align-middle">calendar_today</i>
        <span>Date</span>
      </div>

      <div
        v-show="showTenantsWidget"
        class="widget-button"
        title="Tenants"
        @click.prevent="showCreateTenantsModal = true"
      >
        <i class="material-icons-outlined align-middle">table_view</i>
        <span>Tenants</span>
      </div>

      <div class="widget-button" title="Weather" @click.prevent="showCreateWeatherModal = true">
        <i class="material-icons-outlined align-middle">wb_sunny</i>
        <span>Weather</span>
      </div>

      <div class="widget-button" title="Trafiklab" @click.prevent="showCreateTrafiklabModal = true">
        <i class="material-icons-outlined align-middle">train</i>
        <span>Trafiklab</span>
      </div>

      <div
        class="widget-button"
        title="PDF"
        @click.prevent="openContentBrowser(WIDGET_CONTENT_TYPE.PDF)"
      >
        <i class="material-icons-outlined align-middle">picture_as_pdf</i>
        <span>PDF</span>
      </div>

      <div
        class="widget-button"
        title="Audio"
        @click.prevent="openContentBrowser(WIDGET_CONTENT_TYPE.AUDIO)"
      >
        <i class="material-icons-outlined align-middle">audio_file</i>
        <span>Audio</span>
      </div>

      <div class="widget-button" title="Link" @click.prevent="showCreateLinkModal = true">
        <i class="material-icons-outlined align-middle">link</i>
        <span>URL</span>
      </div>

      <div class="widget-button" title="Layer" @click.prevent="createLayer">
        <i class="material-icons-outlined align-middle">layers</i>
        <span>Shape Layer</span>
      </div>

      <div class="widget-button" title="RSS Viewer" @click.prevent="showCreateRssModal = true">
        <i class="material-icons-outlined align-middle">rss_feed</i>
        <span>RSS</span>
      </div>

      <div class="widget-button" title="Moving Text" @click.prevent="createText">
        <i class="material-icons-outlined align-middle">text_fields</i>
        <span>Moving Text</span>
      </div>

      <div class="widget-button" title="Button" @click.prevent="createButton">
        <i class="material-icons-outlined align-middle">smart_button</i>
        <span>Button</span>
      </div>

      <!-- <div class="widget-button" title="Table" @click.prevent="showCreateTableModal = true">
        <i class="material-icons-outlined align-middle">table_view</i>
        <span>Table</span>
      </div> -->
    </div>

    <CreateTenantsWidgetModal
      v-if="showCreateTenantsModal"
      @closeModal="showCreateTenantsModal = false"
      @createTenantsWidget="createTenant"
    />

    <CreateTrafiklabWidgetModal
      v-if="showCreateTrafiklabModal"
      @closeModal="showCreateTrafiklabModal = false"
      @createWidget="createTrafiklabWidget"
    />

    <CreateWeatherWidgetModal
      v-if="showCreateWeatherModal"
      @closeModal="showCreateWeatherModal = false"
      @createWidget="createWeatherWidget"
    />

    <CreateRssWidgetModal
      v-if="showCreateRssModal"
      @closeModal="showCreateRssModal = false"
      @createWidget="createRssWidget"
    />

    <CreateLinkWidgetModal
      v-if="showCreateLinkModal"
      @closeModal="showCreateLinkModal = false"
      @createWidget="createLinkWidget"
    />

    <TableWidgetModal
      v-if="showCreateTableModal"
      @closeModal="showCreateTableModal = false"
      @createTableWidget="createTableWidget"
    />

    <CreateTimeWidgetModal
      v-if="showCreateTimeModal"
      @closeModal="showCreateTimeModal = false"
      @createWidget="createTimeWidget"
    />

    <CreateDateWidgetModal
      v-if="showCreateDateModal"
      @closeModal="showCreateDateModal = false"
      @createWidget="createDateWidget"
    />

    <ContentBrowserModal
      ref="content_browser_modal"
      @submitted="[createContentWidget($event), closeContentBrowser()]"
      :allow-multiple-item-select="false"
      :filterItemTypes="filterItemTypes[selectedItemType]"
    >
    </ContentBrowserModal>
  </div>
</template>

<script>
  import config from '@/config';

  import { TEMPLATE_DESIGNER_ADD_WIDGET } from '@/store/actions/templateDesigner';

  import {
    ALIGN_MODES,
    generateDefaultWidgetData,
    IMAGE_MODE,
    WIDGET_CONTENT_TYPE,
    WIDGET_TYPES,
  } from '@/models/layoutDesigner';

  import ContentBrowserModal from '@/components/content/ContentBrowserModal.vue';
  import Button from '@/components/common/Button.vue';
  import CreateTenantsWidgetModal from '@/components/apps/tenants/CreateTenantsWidgetModal.vue';
  import CreateTrafiklabWidgetModal from '@/components/apps/trafiklab/CreateTrafiklabWidgetModal.vue';
  import CreateWeatherWidgetModal from '@/components/apps/weather/CreateWeatherWidgetModal.vue';
  import CreateRssWidgetModal from '@/components/apps/rss/CreateRssWidgetModal.vue';
  import CreateLinkWidgetModal from '@/components/apps/link/CreateLinkWidgetModal.vue';
  import TableWidgetModal from '@/components/apps/table/TableWidgetModal.vue';
  import CreateTimeWidgetModal from '@/components/apps/time/CreateTimeWidgetModal.vue';
  import CreateDateWidgetModal from '@/components/apps/date/CreateDateWidgetModal.vue';

  import { apiGetDetailedAddress } from '@/api/tenants';

  import { getEncodedURL } from '@/helpers/mixins';
  import { CONTENT_TYPE_WEATHER_APP } from '@/constant/const';
  import { tenantsDemoData, tenantsDefaultOptions } from '@/constant/tenants';
  import { weatherDefaultOptions } from '@/constant/weatherApp';
  import { DEFAULT_TRAFIKLAB_VIEWER_STYLE } from '@/constant/trafiklabWidget';
  import { timeDefaultOptions } from '@/constant/timeWidget';

  export default {
    name: 'WidgetToolbar',

    components: {
      ContentBrowserModal,
      Button,
      CreateTenantsWidgetModal,
      CreateTrafiklabWidgetModal,
      CreateWeatherWidgetModal,
      CreateRssWidgetModal,
      CreateLinkWidgetModal,
      TableWidgetModal,
      CreateTimeWidgetModal,
      CreateDateWidgetModal,
    },

    data() {
      return {
        filterItemTypes: {
          [WIDGET_CONTENT_TYPE.IMAGE]: [WIDGET_CONTENT_TYPE.IMAGE],
          [WIDGET_CONTENT_TYPE.APP]: [WIDGET_CONTENT_TYPE.APP],
          [WIDGET_CONTENT_TYPE.VIDEO]: [WIDGET_CONTENT_TYPE.VIDEO],
          [WIDGET_CONTENT_TYPE.AUDIO]: [WIDGET_CONTENT_TYPE.AUDIO],
          [WIDGET_CONTENT_TYPE.PDF]: [WIDGET_CONTENT_TYPE.PDF],
        },
        selectedItemType: 'image',
        WIDGET_CONTENT_TYPE,
        showCreateTenantsModal: false,
        showCreateTrafiklabModal: false,
        showCreateWeatherModal: false,
        showCreateRssModal: false,
        showCreateLinkModal: false,
        showCreateTableModal: false,
        showCreateTimeModal: false,
        showCreateDateModal: false,
      };
    },

    computed: {
      showTenantsWidget() {
        const { organisation } = this.$store.state.organisation;

        return !!organisation?.statistics?.integrations.includes('TenantIntegration');
      },

      template() {
        return this.$store.state.templateDesigner.template;
      },

      nextZIndex() {
        return Object.keys(this.$store.state.templateDesigner.widgets).length;
      },
    },

    methods: {
      prepareAppData(widgetType, override = {}) {
        const defaultWidget = generateDefaultWidgetData(widgetType, {
          width: 50,
          height: 50,
          ...override,
        });

        const position = this.getPositionData(defaultWidget);

        return {
          templateId: this.template.template_id,
          widget: defaultWidget,
          position,
          widgetData: {
            type: widgetType,
            name: defaultWidget.name,
            config: defaultWidget,
          },
        };
      },

      createLayer() {
        const defaultLayerWidget = generateDefaultWidgetData(WIDGET_TYPES.LAYER, {
          width: 50,
          height: 50,
        });

        const newWidget = {
          type: WIDGET_TYPES.LAYOUT_ITEM,
          object: defaultLayerWidget,
          position: this.getPositionData(defaultLayerWidget),
          itemType: WIDGET_TYPES.LAYER,
        };

        this.$store.commit(TEMPLATE_DESIGNER_ADD_WIDGET, newWidget);
      },

      createButton() {
        const { widget, position, widgetData } = this.prepareAppData(WIDGET_TYPES.BUTTON, {
          height: 18,
          width: 30,
        });

        widgetData.buttonText = widget.text;

        const newWidget = {
          type: WIDGET_TYPES.LAYOUT_ITEM,
          object: widget,
          position,
          itemType: WIDGET_TYPES.BUTTON,
          widgetData,
        };

        this.$store.commit(TEMPLATE_DESIGNER_ADD_WIDGET, newWidget);
      },

      createText() {
        const { widget, position, widgetData } = this.prepareAppData(WIDGET_TYPES.TEXT);

        widgetData.userText = widget.userText;

        const newWidget = {
          widgetData,
          type: WIDGET_TYPES.LAYOUT_ITEM,
          object: widget,
          position,
          itemType: WIDGET_TYPES.TEXT,
        };

        this.$store.commit(TEMPLATE_DESIGNER_ADD_WIDGET, newWidget);
      },

      createRichText() {
        const { widget, position, widgetData } = this.prepareAppData(WIDGET_TYPES.RICH_TEXT);

        widgetData.userText = widget.userText;

        const newWidget = {
          widgetData,
          type: WIDGET_TYPES.LAYOUT_ITEM,
          object: widget,
          position,
          itemType: WIDGET_TYPES.RICH_TEXT,
        };

        this.$store.commit(TEMPLATE_DESIGNER_ADD_WIDGET, newWidget);
      },

      async createTenant(configData) {
        const { widget, position, widgetData } = this.prepareAppData(WIDGET_TYPES.TENANTS, {
          name: configData.name,
          tenants: {
            ...tenantsDefaultOptions,
            ...configData,
            floors:
              configData.address.id === 'demo'
                ? tenantsDemoData
                : await this.fetchTenants(configData.address.id),
          },
          width: 100,
        });

        const newWidget = {
          widgetData,
          type: WIDGET_TYPES.LAYOUT_ITEM,
          object: widget,
          position,
          itemType: WIDGET_TYPES.TENANTS,
        };

        this.$store.commit(TEMPLATE_DESIGNER_ADD_WIDGET, newWidget);
      },

      async fetchTenants(addressId) {
        try {
          const response = await apiGetDetailedAddress(addressId);
          const floors = response.floors.map((floor) => {
            const floorDefaultName = floor.floor !== 0 ? `VÅNING ${floor.floor}` : 'Bottenvåning';

            return {
              ...floor,
              displayFloorName: floor.displayFloorName || floorDefaultName,
              visible: true,
            };
          });

          return floors;
        } catch (error) {
          this.$toasted.global.general_error({
            message: 'Could not get tenants information.',
          });

          return tenantsDemoData;
        }
      },

      createTrafiklabWidget(configData) {
        const { widget, position, widgetData } = this.prepareAppData(WIDGET_TYPES.TRAFIKLABV2, {
          name: configData.name,
          timetable: [
            {
              hideTime: 0,
              language: 'sv',
              playTime: '30',
              show: false,
              stop: configData.stop,
              trafficTypes: {
                BUS: configData.stop.availableForBus,
                METRO: configData.stop.availableForMetro,
                TRAIN: configData.stop.availableForTrain,
                TRAM: configData.stop.availableForTram,
                SHIP: configData.stop.availableForShip,
              },
            },
          ],
          viewerStyles: DEFAULT_TRAFIKLAB_VIEWER_STYLE,
        });

        const newWidget = {
          widgetData,
          type: WIDGET_TYPES.LAYOUT_ITEM,
          object: widget,
          position,
          itemType: WIDGET_TYPES.TRAFIKLABV2,
        };

        this.$store.commit(TEMPLATE_DESIGNER_ADD_WIDGET, newWidget);
      },

      async createWeatherWidget(configData) {
        const { widget, position, widgetData } = this.prepareAppData(WIDGET_TYPES.WEATHERV2, {
          name: configData.name,
          weather: {
            ...weatherDefaultOptions,
            type: configData.displayType,
            name: configData.name,
            location: configData.location,
          },
          width: 100,
        });

        const newWidget = {
          widgetData,
          type: WIDGET_TYPES.LAYOUT_ITEM,
          object: widget,
          position,
          itemType: WIDGET_TYPES.WEATHERV2,
        };

        this.$store.commit(TEMPLATE_DESIGNER_ADD_WIDGET, newWidget);
      },

      createTimeWidget(configData) {
        const { widget, position, widgetData } = this.prepareAppData(WIDGET_TYPES.TIME, {
          name: configData.name,
          time: {
            ...timeDefaultOptions,
            ...configData.time,
          },
          width: 30,
          height: 15,
        });

        const newWidget = {
          widgetData,
          type: WIDGET_TYPES.LAYOUT_ITEM,
          object: widget,
          position,
          itemType: WIDGET_TYPES.TIME,
        };

        this.$store.commit(TEMPLATE_DESIGNER_ADD_WIDGET, newWidget);

        this.showCreateTimeModal = false;
      },

      createDateWidget(configData) {
        const { widget, position, widgetData } = this.prepareAppData(WIDGET_TYPES.DATE, {
          name: configData.name,
          date: configData.date,
        });

        const newWidget = {
          widgetData,
          type: WIDGET_TYPES.LAYOUT_ITEM,
          object: widget,
          position,
          itemType: WIDGET_TYPES.DATE,
        };

        this.$store.commit(TEMPLATE_DESIGNER_ADD_WIDGET, newWidget);

        this.showCreateDateModal = false;
      },

      createLinkWidget(configData) {
        const { widget, position, widgetData } = this.prepareAppData(WIDGET_TYPES.LINK, {
          name: configData.name,
          link: configData.link,
        });

        const newWidget = {
          widgetData,
          type: WIDGET_TYPES.LAYOUT_ITEM,
          object: widget,
          position,
          itemType: WIDGET_TYPES.LINK,
        };

        this.$store.commit(TEMPLATE_DESIGNER_ADD_WIDGET, newWidget);
      },

      createTableWidget(configData) {
        const { widget, position, widgetData } = this.prepareAppData(WIDGET_TYPES.TABLEV2, {
          name: configData.name,
          columns: configData.columns,
          rows: configData.rows,
          styles: {
            heading: {
              alignment: ALIGN_MODES.CENTER_ALIGN,
              backgroundColor: '#FFFFFF',
              fontSize: 20,
            },
            body: {
              alignment: ALIGN_MODES.CENTER_ALIGN,
              backgroundColor: '#FFFFFF',
              fontSize: 16,
            },
            table: {
              lineWidth: 1,
              linesColor: '#2D2D2D',
              theme: 'black-white',
            },
            pagination: {
              per_page: 20,
              interval: 10,
            },
          },
        });

        const newWidget = {
          widgetData,
          type: WIDGET_TYPES.LAYOUT_ITEM,
          object: widget,
          position,
          itemType: WIDGET_TYPES.TABLEV2,
        };

        this.$store.commit(TEMPLATE_DESIGNER_ADD_WIDGET, newWidget);
      },

      createImage(item) {
        const imageWidget = generateDefaultWidgetData(WIDGET_TYPES.IMAGE, {
          uuid: item.item_id,
          item_url: item.item_url,
          item_id: item.item_id,
          width: 50,
          height: 50,
        });

        const widgetPositions = this.getPositionData(imageWidget);

        const position = {
          ...imageWidget,
          ...item,
          ...widgetPositions,
        };

        const newWidget = {
          type: WIDGET_TYPES.LAYOUT_ITEM,
          object: imageWidget,
          position,
          itemType: WIDGET_TYPES.IMAGE,
        };

        this.$store.commit(TEMPLATE_DESIGNER_ADD_WIDGET, newWidget);
      },

      async createApp(item) {
        const appInfo = this.prepareAppData(WIDGET_TYPES.APP);
        const itemObject = {
          ...item,
          config: undefined,
          ...appInfo.widget,
          ...item.config,
          type: WIDGET_TYPES.APP,
          item_type: item.item_type,
          item_id: item.item_id,
        };

        if (item.item_type === CONTENT_TYPE_WEATHER_APP) {
          itemObject.weather = item.weather
            ? item.weather
            : {
                ...weatherDefaultOptions,
                name: item.name,
                backgroundColor: item.background?.color,
                language: item.lang,
                temperatureUnit: item.temp_unit,
                timeFormat: item.time_format,
                location: {
                  label: item.name,
                  name: item.name,
                  coordinates: item.location,
                },
              };
        }

        const newWidget = {
          type: WIDGET_TYPES.LAYOUT_ITEM,
          position: appInfo.position,
          object: itemObject,
          itemType: WIDGET_TYPES.APP,
        };

        this.$store.commit(TEMPLATE_DESIGNER_ADD_WIDGET, newWidget);
      },

      async createVideo(item) {
        const videoWidget = generateDefaultWidgetData(WIDGET_TYPES.VIDEO, {
          uuid: item.item_id,
          item_id: item.item_id,
          width: 50,
          height: 50,
        });

        const widgetPositions = this.getPositionData(videoWidget);

        const position = {
          ...widgetPositions,
          data: {
            ...videoWidget,
            ...item,
            thumbnail_url: getEncodedURL(config.baseUrl, item.thumbnail_url),
            item_url: getEncodedURL(config.baseUrl, item.item_url),
          },
        };

        const newWidget = {
          type: WIDGET_TYPES.LAYOUT_ITEM,
          position,
          object: videoWidget,
          itemType: WIDGET_TYPES.VIDEO,
        };

        this.$store.commit(TEMPLATE_DESIGNER_ADD_WIDGET, newWidget);
      },

      async createAudio(item) {
        const audioWidget = generateDefaultWidgetData(WIDGET_TYPES.AUDIO, {
          uuid: item.item_id,
          item_id: item.item_id,
        });

        const widgetPositions = this.getPositionData(audioWidget);

        const position = {
          ...widgetPositions,
          data: {
            ...audioWidget,
            ...item,
            item_url: getEncodedURL(config.baseUrl, item.item_url),
          },
        };

        const newWidget = {
          type: WIDGET_TYPES.LAYOUT_ITEM,
          position,
          object: audioWidget,
          itemType: WIDGET_TYPES.AUDIO,
        };

        this.$store.commit(TEMPLATE_DESIGNER_ADD_WIDGET, newWidget);
      },

      async createPdf(item) {
        if (!item.item_id) {
          this.$toasted.global.general_error({ message: 'Something is wrong with your PDF!' });

          return;
        }

        const pdfWidget = generateDefaultWidgetData(WIDGET_TYPES.PDF, {
          uuid: item.item_id,
          item_id: item.item_id,
          width: 50,
          height: 50,
        });

        const widgetPositions = this.getPositionData(pdfWidget);

        let position = {
          ...pdfWidget,
          ...widgetPositions,
          data: { ...item, pageDuration: 15, imageMode: IMAGE_MODE.CONTAIN },
        };

        if (item.pdf_content) {
          position = { ...position, data: { ...position.data, ...item.pdf_content } };
        }

        const newWidget = {
          type: WIDGET_TYPES.LAYOUT_ITEM,
          position,
          object: pdfWidget,
          itemType: WIDGET_TYPES.PDF,
        };

        this.$store.commit(TEMPLATE_DESIGNER_ADD_WIDGET, newWidget);
      },

      async createContentWidget(selectedItems) {
        if (selectedItems.length === 0) return;

        const item = selectedItems[0];

        switch (this.selectedItemType) {
          case WIDGET_CONTENT_TYPE.IMAGE:
            await this.createImage(item);
            break;
          case WIDGET_CONTENT_TYPE.APP:
            await this.createApp(item);
            break;
          case WIDGET_CONTENT_TYPE.VIDEO:
            await this.createVideo(item);
            break;
          case WIDGET_CONTENT_TYPE.AUDIO:
            await this.createAudio(item);
            break;
          case WIDGET_CONTENT_TYPE.PDF:
            await this.createPdf(item);
            break;
        }
      },

      openContentBrowser(type = 'image') {
        this.selectedItemType = type;
        this.$refs.content_browser_modal.open();
      },

      closeContentBrowser() {
        this.$refs.content_browser_modal.close();
      },

      getPositionData(widget) {
        const { width = 50, height = 50 } = widget;

        const commonPosition = {
          zIndex: this.nextZIndex,
          width,
          height,
          x: 0,
          y: 0,
          usePixels: true,
          xPixels: 0,
          yPixels: 0,
        };

        const xScale = width / 100;
        const yScale = height / 100;
        const widgetWidth = xScale * this.template.settings.width;
        const widgetHeight = yScale * this.template.settings.height;

        if (widget.width) delete widget.width;
        if (widget.height) delete widget.height;

        return {
          ...commonPosition,
          widthPixels: widgetWidth,
          heightPixels: widgetHeight,
        };
      },
    },
  };
</script>

<style lang="scss" scoped>
  .widget-toolbar {
    a.btn {
      width: 44px;
      height: 42px;

      &:focus {
        box-shadow: none;
      }

      &:hover {
        background-color: #d0d0d0;
      }
    }
  }

  .widget-buttons {
    display: flex;
    flex-wrap: wrap;
    gap: 14px 8px;

    .widget-button {
      display: flex;
      align-items: center;
      gap: 6px;

      font-weight: 500;
      font-size: 14px;
      line-height: 24px;
      padding: 4px 12px;
      background-color: $highlightGrey;
      color: $tableText;
      border-radius: 20px;

      &:hover {
        cursor: pointer;
        background-color: $borderGrey;
      }
    }
  }
</style>
